import React, { useState } from "react";
import { Search, Package, Truck, CheckCircle2, Home } from "lucide-react";
import { trackOrder, formatINR } from "../lib/api";
import { toast } from "sonner";

const STATUS_ICONS = {
  "Order Placed": Package,
  "Confirmed": CheckCircle2,
  "Packed": Package,
  "Shipped": Truck,
  "Out for Delivery": Truck,
  "Delivered": CheckCircle2,
  "Cancelled": Home,
  // lowercase forms written by admin panel
  "confirmed": CheckCircle2,
  "packed": Package,
  "shipped": Truck,
  "out_for_delivery": Truck,
  "delivered": CheckCircle2,
  "cancelled": Home,
};

const prettyStatus = (s) => {
  if (!s) return s;
  if (/[A-Z]/.test(s)) return s;
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [emailField, setEmailField] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) return;
    setLoading(true);
    setOrder(null);
    try {
      const o = await trackOrder(orderNumber.trim(), emailField.trim() || undefined);
      setOrder(o);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) toast.error("Please enter the email on your order, or sign in.");
      else if (status === 429) toast.error("Too many requests. Please wait a minute and try again.");
      else toast.error("Order not found. Please check the order number.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="track-order-page">
      <p className="label-tiny text-brand-gold">Order Tracking</p>
      <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2">Where's my Bindni?</h1>
      <p className="text-brand-muted mt-3">Enter your order number (starts with BJ) and the email you used to order. Signed-in customers don't need the email.</p>

      <form onSubmit={track} className="mt-8 space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Order number e.g. BJ240101ABC123" className="w-full pl-11 pr-4 py-3.5 bg-brand-ivory-soft border border-brand-gold/40 outline-none focus:border-brand-maroon" data-testid="track-order-input" />
        </div>
        <input value={emailField} onChange={(e) => setEmailField(e.target.value)} type="email" placeholder="Email on your order (optional if signed in)" className="w-full px-4 py-3.5 bg-brand-ivory-soft border border-brand-gold/40 outline-none focus:border-brand-maroon" data-testid="track-order-email" />
        <button disabled={loading} className="btn-primary w-full sm:w-auto" data-testid="track-order-btn">{loading ? "Tracking…" : "Track Order"}</button>
      </form>

      {order && (
        <div className="mt-12 border border-brand-gold/30 bg-brand-ivory-soft p-8" data-testid="order-details">
          <div className="flex flex-wrap justify-between gap-4 pb-6 border-b border-brand-gold/25">
            <div>
              <p className="label-tiny text-brand-gold">Order Number</p>
              <p className="font-serif-brand text-2xl text-brand-charcoal mt-1">{order.order_number}</p>
            </div>
            <div className="text-right">
              <p className="label-tiny text-brand-gold">Total</p>
              <p className="font-serif-brand text-2xl text-brand-maroon mt-1">{formatINR(order.total)}</p>
            </div>
          </div>

          <div className="mt-8">
            <p className="label-tiny text-brand-charcoal mb-6">Tracking Timeline</p>
            <div className="space-y-4">
              {order.tracking_updates.map((u, i) => {
                const Icon = STATUS_ICONS[u.status] || Package;
                return (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-maroon text-brand-ivory flex items-center justify-center shrink-0"><Icon size={16} /></div>
                    <div>
                      <p className="font-serif-brand text-lg text-brand-charcoal">{prettyStatus(u.status)}</p>
                      <p className="text-xs text-brand-muted">{u.note} · {new Date(u.at).toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-brand-gold/25">
            <p className="label-tiny text-brand-gold">Delivering to</p>
            <p className="mt-2 text-brand-charcoal">{order.shipping.full_name}, {order.shipping.line1}, {order.shipping.city}, {order.shipping.state} - {order.shipping.pincode}</p>
          </div>

          <div className="mt-6 pt-6 border-t border-brand-gold/25">
            <p className="label-tiny text-brand-gold mb-3">Items ({order.items.length})</p>
            <div className="space-y-3">
              {order.items.map((it, i) => (
                <div key={i} className="flex gap-3">
                  <img src={it.image} alt="" className="w-14 h-14 object-cover" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-brand-charcoal">{it.name}</p>
                    <p className="text-xs text-brand-muted">Qty {it.quantity} · {formatINR(it.price)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
